import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import {
  getGruposPlanta, createGrupoPlanta, updateGrupoPlanta, deleteGrupoPlanta, getPersonasArea, getOtConfig,
} from '../api/client'
import PageHeader from '../components/PageHeader'

// Operaciones > Administración > Grupos de trabajo: each group's people,
// picked from the register's Planta and Mantenimiento areas, and its
// supervisor -- always one of its members. A person is in one group at most:
// "Agregar persona" offers only who isn't in any group yet. A request
// may propose a group; planning confirms it. Editing takes the function
// "administrar_grupos"; everyone else sees them read-only.
export default function PlantaGrupos() {
  const { t } = useTranslation(['operations', 'common'])
  const navigate = useNavigate()
  const [grupos, setGrupos] = useState(null)
  const [personas, setPersonas] = useState([])
  const [puede, setPuede] = useState(false)
  const [verInactivos, setVerInactivos] = useState(false)
  const [nuevo, setNuevo] = useState('')
  const [error, setError] = useState('')

  const cargar = useCallback(() => {
    getGruposPlanta({ incluir_inactivos: true }).then(setGrupos).catch(() => setError(t('ot.grupos.cargaFallo')))
  }, [t])
  useEffect(() => { cargar() }, [cargar])
  useEffect(() => {
    Promise.all([getPersonasArea('planta'), getPersonasArea('mantenimiento')]).then(([a, b]) => {
      const vistos = new Set()
      setPersonas([...a, ...b].filter(x => !vistos.has(x.id) && vistos.add(x.id))
        .sort((x, y) => x.nombre.localeCompare(y.nombre, 'es')))
    }).catch(() => {})
    getOtConfig().then(c => setPuede(c.funciones.includes('administrar_grupos'))).catch(() => {})
  }, [])

  const guardar = async (g, cambios) => {
    setError('')
    try {
      const nuevoG = await updateGrupoPlanta(g.id, cambios)
      setGrupos(gs => gs.map(x => (x.id === g.id ? nuevoG : x)))
    } catch (err) {
      const d = err.response?.data?.detail
      setError(d?.code === 'nombre_en_uso' ? t('ot.grupos.nombreEnUso', { nombre: cambios.nombre })
        : d?.code === 'supervisor_no_miembro' ? t('ot.grupos.supervisorNoMiembro') : d?.message || t('ot.panel.fallo'))
    }
  }
  const crear = async (e) => {
    e.preventDefault()
    const nombre = nuevo.trim()
    if (!nombre) return
    setError('')
    try { const g = await createGrupoPlanta({ nombre }); setGrupos(gs => [...gs, g]); setNuevo('') }
    catch (err) {
      const d = err.response?.data?.detail
      setError(d?.code === 'nombre_en_uso' ? t('ot.grupos.nombreEnUso', { nombre }) : d?.message || t('ot.panel.fallo'))
    }
  }
  const borrar = async (g) => {
    if (!window.confirm(t('ot.grupos.borrarConfirm', { nombre: g.nombre }))) return
    try { await deleteGrupoPlanta(g.id); setGrupos(gs => gs.filter(x => x.id !== g.id)) }
    catch (err) { setError(err.response?.data?.detail?.message || t('ot.panel.fallo')) }
  }

  const visibles = (grupos || []).filter(g => verInactivos || g.activo)
  // Who is already in a group (an inactive one too): not offered for another.
  const enGrupo = new Set((grupos || []).flatMap(g => g.miembros.map(m => m.id)))
  const sinGrupo = personas.filter(p => !enGrupo.has(p.id))

  return (
    <div className="p-8">
      <div className="mb-6">
        <PageHeader title={t('ot.grupos.titulo')} subtitle={t('ot.grupos.subtitulo')} />
      </div>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        {puede && (
          <form onSubmit={crear} className="flex items-center gap-2">
            <input value={nuevo} onChange={e => setNuevo(e.target.value)} placeholder={t('ot.grupos.nuevoPlaceholder')}
              className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm bg-white w-56" />
            <button type="submit" disabled={!nuevo.trim()}
              className="bg-gray-900 text-white px-3.5 py-1.5 rounded-lg text-sm font-medium hover:bg-gray-800 disabled:opacity-40">
              {t('ot.grupos.crear')}
            </button>
          </form>
        )}
        <label className="inline-flex items-center gap-1.5 text-sm text-gray-600 cursor-pointer">
          <input type="checkbox" checked={verInactivos} onChange={e => setVerInactivos(e.target.checked)} />
          {t('ot.grupos.verInactivos')}
        </label>
        {puede && grupos && personas.length > 0 && (
          <span className="ml-auto text-xs text-gray-500">{t('ot.grupos.nSinGrupo', { count: sinGrupo.length })}</span>
        )}
        <button type="button" onClick={() => navigate('/admin/personas')} className={`${puede && grupos && personas.length ? '' : 'ml-auto '}text-sm text-indigo-600 hover:underline`}>
          {t('ot.grupos.irPersonas')}
        </button>
      </div>
      {!personas.length && grupos && (
        <p className="mb-4 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-900">{t('ot.grupos.sinPersonas')}</p>
      )}
      {error && <p className="mb-3 text-sm text-red-600">{error}</p>}
      {!grupos ? <p className="text-sm text-gray-400">{t('common:loading', { defaultValue: 'Cargando…' })}</p> : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {visibles.map(g => (
            <Grupo key={g.id} g={g} libres={sinGrupo} puede={puede} t={t}
              onGuardar={c => guardar(g, c)} onBorrar={() => borrar(g)} />
          ))}
        </div>
      )}
    </div>
  )
}

function Grupo({ g, libres, puede, t, onGuardar, onBorrar }) {
  const [nombre, setNombre] = useState(g.nombre)
  const ids = g.miembros.map(m => m.id)
  const renombrar = () => {
    const n = nombre.trim()
    if (!n || n === g.nombre) { setNombre(g.nombre); return }
    onGuardar({ nombre: n })
  }
  return (
    <section className={`rounded-xl border bg-white p-4 ${g.activo ? 'border-gray-200' : 'border-dashed border-gray-300 opacity-70'}`}>
      <div className="flex items-center justify-between gap-2">
        {puede ? (
          <input value={nombre} onChange={e => setNombre(e.target.value)} onBlur={renombrar}
            onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); if (e.key === 'Escape') setNombre(g.nombre) }}
            className="text-base font-semibold text-gray-900 bg-transparent border-b border-transparent hover:border-gray-200 focus:border-gray-400 focus:outline-none min-w-0 flex-1" />
        ) : <h2 className="text-base font-semibold text-gray-900">{g.nombre}</h2>}
        <span className="text-[11px] text-gray-400 shrink-0">{t('ot.grupos.nOts', { count: g.n_ots })}</span>
      </div>

      <div className="mt-3">
        <span className="block text-xs font-medium text-gray-500 mb-1">{t('ot.grupos.supervisor')}</span>
        {puede ? (
          <select className="input" value={g.supervisor?.id ?? ''}
            onChange={e => onGuardar({ supervisor_id: e.target.value ? Number(e.target.value) : null })}>
            <option value="">{g.miembros.length ? t('ot.grupos.sinSupervisor') : t('ot.grupos.supervisorPrimero')}</option>
            {g.miembros.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
          </select>
        ) : <p className="text-sm text-gray-800">{g.supervisor?.nombre || '—'}</p>}
      </div>

      <div className="mt-3">
        <span className="block text-xs font-medium text-gray-500 mb-1">{t('ot.grupos.personal', { n: g.miembros.length })}</span>
        <div className="flex flex-wrap gap-1.5">
          {g.miembros.map(m => (
            <span key={m.id} className="inline-flex items-center gap-1 rounded-full bg-gray-100 pl-2.5 pr-1 py-0.5 text-xs text-gray-800">
              {m.nombre}
              {puede && (
                <button type="button" onClick={() => onGuardar({ miembros: ids.filter(x => x !== m.id) })}
                  title={t('ot.grupos.quitar')} className="w-4 h-4 rounded-full hover:bg-black/10 leading-none">×</button>
              )}
            </span>
          ))}
          {!g.miembros.length && <span className="text-xs text-gray-400">{t('ot.grupos.sinPersonal')}</span>}
        </div>
        {puede && !libres.length && <p className="mt-2 text-[11px] text-gray-400">{t('ot.grupos.todosConGrupo')}</p>}
        {puede && libres.length > 0 && (
          <select value="" onChange={e => e.target.value && onGuardar({ miembros: [...ids, Number(e.target.value)] })}
            className="mt-2 text-xs border border-gray-200 rounded-md px-2 py-1 bg-white text-gray-600">
            <option value="">{t('ot.grupos.agregarPersona')}</option>
            {libres.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
          </select>
        )}
      </div>

      {puede && (
        <div className="mt-4 flex items-center justify-between gap-2 border-t border-gray-100 pt-3">
          <label className="inline-flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer">
            <input type="checkbox" checked={g.activo} onChange={e => onGuardar({ activo: e.target.checked })} />
            {t('ot.grupos.activo')}
          </label>
          {g.n_ots === 0 && (
            <button type="button" onClick={onBorrar} className="text-xs text-gray-400 hover:text-red-600">{t('ot.grupos.borrar')}</button>
          )}
        </div>
      )}
    </section>
  )
}
